import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useRateHistory(loanId: string) {
  return useQuery({
    queryKey: ['rate-history', loanId],
    queryFn: async () => {
      const { data } = await api.get(`/loans/${loanId}/rate-history`);
      return data;
    },
    enabled: !!loanId,
  });
}

// Custom action endpoint /rate-change — keep manual
export function useChangeRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      loanId: string;
      newRate: number;
      reason: string;
      margin?: number;
    }) => {
      const { data } = await api.post(`/loans/${params.loanId}/rate-change`, {
        newRate: params.newRate,
        reason: params.reason,
        margin: params.margin,
      });
      return data;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['rate-history', loanId] });
      qc.invalidateQueries({ queryKey: ['loans', loanId], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'cashflows'], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'npv'] });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'yield'], exact: true });
    },
  });
}

export function useBaseRates() {
  return useQuery({
    queryKey: ['base-rates'],
    queryFn: async () => {
      const { data } = await api.get('/loans/base-rates');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Custom action endpoint with PUT — keep manual
export function useUpdateBaseRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, rate }: { name: string; rate: number }) => {
      const { data } = await api.put(`/loans/base-rates/${name}`, { rate });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['base-rates'] });
      qc.invalidateQueries({ queryKey: ['rate-history'] });
    },
  });
}

export function useCreateBaseRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, rate }: { name: string; rate: number }) => {
      const { data } = await api.post('/loans/base-rates', { name, rate });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['base-rates'] });
    },
  });
}

export function useRateReviews(days?: number) {
  return useQuery({
    queryKey: ['rate-reviews', days],
    queryFn: async () => {
      const { data } = await api.get('/loans/rate-reviews', {
        params: days ? { days } : {},
      });
      return data;
    },
  });
}
